
<template>
    <div class="tc-toolbar" :style="styles"
        :key="tool_count">
            <div class="tc-util-item tc-cursor-item" :style="cursorItemStyle">
                <button class="tc-util-btn" :class="{active: utilOpen==='cursor'}"
                    @click="openCursor($event)"
                    :title="'Cursor mode: '+currentCursorMode" aria-label="Cursor mode">
                    <svg :width="config.TB_ICON" :height="config.TB_ICON" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M12 2v4M12 18v4M2 12h4M18 12h4"/><circle cx="12" cy="12" r="2.6"/></svg>
                </button>
                <Teleport to="body"><div v-if="utilOpen==='cursor'" class="tc-util-pop" :style="cursorPosition" @mousedown.stop>
                    <button v-for="opt in cursorModes" :key="opt" :class="{active: opt===currentCursorMode}" @click="pickCursor(opt)">{{ opt }}</button>
                </div></Teleport>
            </div>
        <div class="tc-tools-scroll"><template v-for="(tool, i) in toolbarGroups" :key="tool.group || tool.type"><toolbar-item
            v-if="tool.icon && !tool.hidden && tool.type !== 'Cursor'"
            @item-selected="selected"

            :data="tool"
            :subs="sub_map"
            :dc="data"
            :config="config"
            :colors="colors"
            :selected="is_selected(tool)">
        </toolbar-item></template>
        <!-- Splitter before the utility icons -->
        <div class="tc-tb-splitter" :style="splitterStyle"></div>
        <div class="tc-drawing-utilities" aria-label="Drawing controls">
            <button class="tc-util-btn" :class="{active: currentMagnet!=='Off'}"
                @click="cycleMagnet" :title="'Magnet: '+currentMagnet" aria-label="Magnet mode">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4h4v9a2 2 0 004 0V4h4v9a6 6 0 01-12 0z"/><path d="M6 8H2M18 8h4"/></svg>
            </button>
            <button class="tc-util-btn" :class="{active:data?.drawingPrefs?.keepDrawing}" @click="utility('KeepDrawing')" title="Stay in drawing mode" aria-label="Stay in drawing mode">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18z"/><rect x="15.5" y="15.5" width="5" height="5" rx="1"/></svg>
            </button>
            <button class="tc-util-btn" :class="{active:data?.drawingPrefs?.locked}" @click="utility('LockDrawings')" title="Lock all drawings" aria-label="Lock all drawings">
                <svg width="16" height="16" viewBox="0 0 24 24" :fill="data?.drawingPrefs?.locked ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="1.8"><rect x="5" y="10" width="14" height="10" rx="1.5"/><path d="M8 10V7a4 4 0 018 0v3"/></svg>
            </button>
            <button class="tc-util-btn" :class="{active:data?.drawingPrefs?.hidden}" @click="utility('HideDrawings')" title="Hide all drawings" aria-label="Hide all drawings">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
        </div>
        </div>
        <div class="tc-drawing-delete-footer">
        <button type="button" class="tc-util-btn" :disabled="!hasDrawings" aria-label="Delete drawings" aria-haspopup="menu" :aria-expanded="utilOpen==='delete'" @click="openDelete($event)" title="Delete drawings"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 7h16M9 7V4h6v3M6 7l1 14h10l1-14M10 11v6M14 11v6"/></svg></button>
        <Teleport to="body"><div v-if="utilOpen==='delete'" class="tc-util-pop tc-delete-menu" :style="cursorPosition" role="menu" @mousedown.stop>
            <button role="menuitem" @click="utility('Remove'); utilOpen=null">Delete selected drawing</button>
            <button role="menuitem" @click="deleteAll(); utilOpen=null">Delete all drawings</button>
        </div></Teleport>
        <slot name="footer"></slot>
        </div>
    </div>
</template>

<script>

import { drawingGroups } from '../workspace/drawing-palette.mjs'
import ToolbarItem from './ToolbarItem.vue'

// Keep related subcategories in a flyout while exposing the reference rail groups.
const GROUP_MERGE = {
    Pitchforks: 'Channels',

    Cycles: 'Patterns',

}
const TOOLBAR_ORDER = ['Cursor', 'Favorites', 'Lines', 'Channels', 'Fibonacci', 'Gann', 'Forecasting', 'Shapes', 'Annotations', 'Patterns', 'Brushes', 'Measurements', 'Positions', 'Volume']


const CURSOR_MODES = ['Cross', 'Dot', 'Arrow', 'Demonstration', 'Magic', 'Eraser']

export default {
    name: 'Toolbar',
    props: [
        'data', 'height', 'colors', 'tv_id', 'config'
    ],
    components: { ToolbarItem },
    mounted() {
        window.addEventListener('tc-drawing-favorites', this.loadFavorites)
        this.loadFavorites()
        document.addEventListener('mousedown', this.onOutsideClick)
    },
    beforeDestroy() {
        window.removeEventListener('tc-drawing-favorites', this.loadFavorites)
        document.removeEventListener('mousedown', this.onOutsideClick)
    },
    methods: {
        loadFavorites() { try { this.favorites = JSON.parse(localStorage.getItem('tc_drawing_favorites') || '[]') } catch { this.favorites = [] } },
        openDelete(event) {
            const rect=event.currentTarget.getBoundingClientRect();
            this.cursorPosition={position:"fixed",left:rect.right+8+"px",top:Math.max(8,rect.bottom-90)+"px",margin:0};
            this.utilOpen=this.utilOpen==='delete'?null:'delete';
        },
        openCursor(event) {
            const rect = event.currentTarget.getBoundingClientRect()
            this.cursorPosition = {position:'fixed', left:rect.right+8+'px', top:Math.max(8, Math.min(rect.top, window.innerHeight-250))+'px', margin:0}
            this.utilOpen = this.utilOpen === 'cursor' ? null : 'cursor'
        },
        utility(type) { this.$emit('custom-event',{event:'tool-selected',args:['System:'+type]}) },
        selected(tool) {
            this.$emit('custom-event', {
                event: 'tool-selected', args: [tool.type]
            })
            if (tool.group) {
                this.sub_map[tool.group] = tool.type
            }
        },
        onOutsideClick(e) {
            if (this.utilOpen && this.$el && !this.$el.contains(e.target) && !e.target.closest('.tc-util-pop')) this.utilOpen = null
        },
        pickCursor(opt) {
            this.utility('Cursor' + opt)
            this.utilOpen = null
        },
        cycleMagnet() {
            const order = ['off', 'weak', 'strong']
            const current = this.data?.drawingPrefs?.magnet || 'off'
            const next = order[(order.indexOf(current) + 1) % order.length]
            this.utility('Magnet' + next[0].toUpperCase() + next.slice(1))
        },
        is_selected(tool) {
            if (!this.data) return false
            if (tool.group) {
                return !!tool.items.find(
                    x => x.type === this.data.tool)
            }
            return tool.type === this.data.tool
        },
        group_key(tool) {
            if (tool.group) return tool.group
            let type = tool.type || ''
            if (type === 'Cursor') return 'Cursor'
            if (type.startsWith('System:')) return 'System'
            return type
        },
        deleteAll() {
            this.$emit('custom-event', {
                event: 'tool-selected', args: ['System:RemoveAll']
            })
        }
    },
    computed: {
        cursorItemStyle() {
            const size = this.config.TB_ICON + this.config.TB_ITEM_M * 2
            const margin = (this.config.TOOLBAR - size) / 2
            return { width: size+'px', height: size+'px', margin: '8px '+margin+'px 0' }
        },
        styles() {
            let colors = this.$props.colors
            let b = this.$props.config.TB_BORDER
            let w = this.$props.config.TOOLBAR - b
            let cb = colors.tbBack || colors.back
            let brd = colors.tbBorder || colors.scale
            let st = this.$props.config.TB_B_STYLE
            return {
                'width': `${w}px`,
                'height': `${this.$props.height - 3}px`,
                'background-color': cb,
                'border-right': `${b}px ${st} ${brd}`
            }
        },
        sorted_groups() {
            return drawingGroups(this.data?.tools || [])
        },
        // Ungrouped single tools (Cursor, System:*) pass through untouched; category
        // groups with a merge target get folded into that target's item list.
        toolbarGroups() {
            const merged = []
            const byName = {}
            for (const g of this.sorted_groups) {
                if (!g.items) { merged.push(g); continue }
                const target = GROUP_MERGE[g.group] || g.group
                let host = byName[target]
                if (!host) { host = { group: target, icon: null, items: [] }; byName[target] = host; merged.push(host) }
                if (!host.icon) host.icon = g.icon
                for (const item of g.items) {
                    if (/brush|highlighter|freehand/i.test(item.name || '')) {
                        let brushes = byName.Brushes
                        if (!brushes) { brushes = {group:'Brushes', icon:item.icon, items:[]}; byName.Brushes = brushes; merged.push(brushes) }
                        brushes.items.push(item)
                    } else if (!host.items.some(x => x.type === item.type)) host.items.push(item)
                }
            }
            const favoriteItems = merged.flatMap(g=>g.items||[]).filter(x=>this.favorites.includes(x.type));
            if (favoriteItems.length) merged.unshift({group:'Favorites',icon:favoriteItems[0].icon,items:favoriteItems});
            return merged.filter(group => !group.items || group.items.length).sort((a,b) => {
                const rank = group => { const index = TOOLBAR_ORDER.indexOf(group.group || group.type); return index < 0 ? 99 : index }
                return rank(a) - rank(b)
            })
        },
        cursorModes() { return CURSOR_MODES },
        currentCursorMode() {
            return (this.data?.drawingPrefs?.cursor || 'cross').replace(/^./, c => c.toUpperCase())
        },
        currentMagnet() {
            return (this.data?.drawingPrefs?.magnet || 'off').replace(/^./, c => c.toUpperCase())
        },
        hasDrawings() {
            let dc = this.data
            if (!dc) return false
            let onchart = dc.onchart || []
            let offchart = dc.offchart || []
            return onchart.some(x => x.settings && x.settings.$state === 'finished')
                || offchart.some(x => x.settings && x.settings.$state === 'finished')
        },
        splitterStyle() {
            let conf = this.$props.config
            let colors = this.$props.colors
            let c = colors.grid
            let im = conf.TB_ITEM_M
            let m = (conf.TOOLBAR - conf.TB_ICON) * 0.5 - im
            let s = conf.TB_ICON + im * 2
            return {
                'width': `${s}px`,
                'height': '1px',
                'margin': `8px ${m}px 8px ${m}px`,
                'background-color': c
            }
        },
        deleteAllStyle() {
            let conf = this.$props.config
            let im = conf.TB_ITEM_M
            let m = (conf.TOOLBAR - conf.TB_ICON) * 0.5 - im
            let s = conf.TB_ICON + im * 2
            return {
                'width': `${s}px`,
                'margin': `4px ${m}px 0px ${m}px`
            }
        }
    },
    watch: {
        // Watching the whole chart deeply also traverses every candle and
        // overlay on cursor/live-price updates. Only tool count is needed.
        'data.tools.length': {
            handler(count) { this.tool_count = count || 0 },
            immediate: true
        }
    },
    data() { return { favorites: [], cursorPosition: {}, tool_count: 0, sub_map: {}, utilOpen: null } }
}

</script>

<style>
.tc-toolbar {
    position: absolute;
    border-right: 1px solid #e0e3eb;
    background: #ffffff;
    z-index: 101;
    padding-top: 3px;
    user-select: none;
    overflow-y: auto;
    overflow-x: hidden;
}

/* Dark theme toolbar */
.chart-page-dark .tc-toolbar {
    background: #1a2035;
    border-right-color: rgba(55, 59, 75, 0.6);
}

.tc-tb-splitter {
    flex-shrink: 0;
}

.tc-tb-delete-all {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4px 0;
    border-radius: 3px;
    cursor: pointer;
    color: #111111;
    transition: all 0.15s ease;
}
.tc-tb-delete-all:hover {
    background: rgba(249, 115, 22, 0.12);
    color: #f97316;
}
.tc-tb-delete-label {
    font-size: 8px;
    font-weight: 600;
    margin-top: 1px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
}

/* Dark theme delete all */
.chart-page-dark .tc-tb-delete-all {
    color: #eef1f6;
}
.chart-page-dark .tc-tb-delete-all:hover {
    background: rgba(249, 115, 22, 0.18);
    color: #f97316;
}
</style>

<style>
.tc-drawing-utilities{display:flex;flex-direction:column;align-items:center;gap:2px;padding:4px 0}
.tc-util-item{position:relative}
.tc-util-btn{display:flex;align-items:center;justify-content:center;width:28px;height:28px;border:0;background:transparent;color:inherit;cursor:pointer;border-radius:4px}
.tc-util-btn:hover{background:rgba(249,115,22,0.12);color:#f97316}
.chart-page-dark .tc-util-btn:hover{background:rgba(249,115,22,0.18);color:#f97316}
.tc-util-btn.active{background:#2962ff22;color:#2962ff}
.tc-util-pop{position:absolute;left:100%;top:0;margin-left:4px;z-index:2147483000;display:flex;flex-direction:column;min-width:120px;background:#fff;border:1px solid #e0e3eb;border-radius:6px;box-shadow:0 6px 18px rgba(0,0,0,0.14);padding:4px;font-size:11px}
.chart-page-dark .tc-util-pop{background:#1a2035;border-color:rgba(55,59,75,0.6);color:#d1d4dc}
.tc-util-pop button{border:0;background:transparent;color:inherit;text-align:left;padding:6px 8px;border-radius:4px;cursor:pointer;font-size:11px}
.tc-util-pop button:hover{background:rgba(249,115,22,0.1);color:#f97316}
.tc-util-pop button.active{background:#2962ff22;color:#2962ff}
</style>

<style>
.tc-cursor-item > .tc-util-btn{width:100%;height:100%;padding:0;box-sizing:border-box}
</style>

<style>
.workspace-page .workspace-drawing-sidebar .tc-toolbar { display:flex; flex-direction:column; overflow:hidden; padding-bottom:0; }
.tc-tools-scroll { flex:1; min-height:0; overflow-y:auto; overflow-x:hidden; scrollbar-width:none; padding-bottom:8px; }
.tc-drawing-delete-footer { flex:none; display:flex; flex-direction:column; align-items:center; gap:4px; padding:8px 0; border-top:1px solid var(--ws-border,#e7e9ee); background:var(--ws-bg,#fff); }
.tc-drawing-delete-footer .tc-tb-splitter { display:none; }
.tc-drawing-delete-footer button { margin:0!important; border-radius:6px; }
.tc-drawing-delete-footer button:hover { background:#f1f2f4!important; }
</style>
